import React, { useState } from 'react';
import { User, Save, Plus, Trash2, GraduationCap, DollarSign } from 'lucide-react';
import { db } from '../../firebase/config';
import { collection, addDoc } from 'firebase/firestore';
import { notifySuccess, notifyError } from '../../utils/alerts';

const RegistroNino = () => {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        nombre: '',
        identificacion: '',
        grado: '',
        valorMensualidad: '',
        acudientes: [
            { nombre: '', telefono: '',  parentesco: '' }
        ]
    });

    // Función para manejar cambios en acudientes específicos
    const handleAcudienteChange = (index, field, value) => {
        const nuevosAcudientes = [...form.acudientes];
        nuevosAcudientes[index][field] = value;
        setForm({ ...form, acudientes: nuevosAcudientes });
    };

    // Añadir nuevo bloque de acudiente
    const addAcudiente = () => {
        setForm({
            ...form,
            acudientes: [...form.acudientes, { nombre: '', telefono: '', parentesco: '' }]
        });
    };

    const removeAcudiente = (index) => {
        if (form.acudientes.length > 1) {
            const filtrados = form.acudientes.filter((_, i) => i !== index);
            setForm({ ...form, acudientes: filtrados });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Obtenemos la fecha de hoy en formato YYYY-MM-DD para proximaFechaPago
        const hoy = new Date().toISOString().split('T')[0];

        try {
            await addDoc(collection(db, "ninos"), {
                ...form,
                valorMensualidad: Number(form.valorMensualidad),
                plan: 'Mensual',
                estadoMembresia: 'Activo',
                fechaRegistro: new Date().toISOString(),
                proximaFechaPago: hoy
            });

            notifySuccess("¡Éxito!", "Estudiante registrado. Su pago vence hoy.");

            // Resetear el form
            setForm({
                nombre: '', identificacion: '', grado: '', valorMensualidad: '', acudientes: [{ nombre: '', telefono: '',  parentesco: '' }]
            });
        } catch (error) {
            console.error(error);
            notifyError("Error", "No se pudo guardar la información.");
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="max-w-4xl mx-auto pb-20 space-y-4 sm:space-y-6">
            <header className="mb-4 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-gray-800 tracking-tight">Nuevo Estudiante</h1>
                <p className="text-sm sm:text-base text-gray-500 font-medium">
                    El plan se activará como <span className="text-blue-600 font-bold">Mensual</span> y la primera fecha de cobro es <span className="text-red-500 font-bold">hoy</span>.
                </p>
            </header>

            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                {/* DATOS DEL NIÑO */}
                <div className="bg-white p-5 sm:p-8 rounded-2xl sm:rounded-[2.5rem] shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-6 sm:mb-8">
                        <div className="p-2 sm:p-3 bg-blue-100 text-blue-600 rounded-xl sm:rounded-2xl"><User size={20} className="sm:w-6 sm:h-6" /></div>
                        <h2 className="text-lg sm:text-xl font-bold text-gray-800">Perfil del Niño</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <div className="space-y-2">
                            <label className="text-[10px] sm:text-xs font-black text-gray-400 uppercase ml-2 tracking-wider">Nombre Completo</label>
                            <input required className="w-full p-4 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-blue-500 rounded-xl sm:rounded-2xl outline-none transition-all text-sm sm:text-base"
                                value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] sm:text-xs font-black text-gray-400 uppercase ml-2 tracking-wider">Identificación</label>
                            <input required className="w-full p-4 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-blue-500 rounded-xl sm:rounded-2xl outline-none transition-all text-sm sm:text-base"
                                value={form.identificacion} onChange={e => setForm({ ...form, identificacion: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] sm:text-xs font-black text-gray-400 uppercase ml-2 tracking-wider">Grado / Nivel</label>
                            <div className="relative">
                                <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    required
                                    className="w-full p-4 pl-10 sm:pl-12 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-blue-500 rounded-xl sm:rounded-2xl outline-none transition-all text-sm sm:text-base"
                                    value={form.grado}
                                    onChange={e => setForm({ ...form, grado: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] sm:text-xs font-black text-green-500 uppercase ml-2 tracking-wider">Costo Mensualidad ($)</label>
                            <div className="relative">
                                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-green-500" size={18} />
                                <input
                                    type="number"
                                    placeholder="Ej: 450000"
                                    required
                                    className="w-full p-4 pl-10 sm:pl-12 bg-green-50/50 border-transparent focus:bg-white focus:ring-2 focus:ring-green-400 rounded-xl sm:rounded-2xl outline-none transition-all font-black text-green-700 text-sm sm:text-base"
                                    value={form.valorMensualidad}
                                    onChange={e => setForm({ ...form, valorMensualidad: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* SECCIÓN ACUDIENTES DINÁMICOS */}
                <div className="space-y-3 sm:space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between px-2 sm:px-4 gap-3">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-800">Contactos de Emergencia</h2>
                        <button type="button" onClick={addAcudiente} className="flex items-center justify-center gap-2 text-xs sm:text-sm font-bold text-blue-600 bg-blue-50 px-4 py-2.5 sm:py-2 rounded-xl hover:bg-blue-100 transition-colors w-full sm:w-auto">
                            <Plus size={16} className="sm:w-[18px] sm:h-[18px]" /> Añadir Acudiente
                        </button>
                    </div>

                    {form.acudientes.map((acudiente, index) => (
                        <div key={index} className="bg-white p-5 sm:p-8 rounded-2xl sm:rounded-[2.5rem] shadow-sm border border-gray-100 relative group animate-in fade-in slide-in-from-bottom-2">
                            {form.acudientes.length > 1 && (
                                <button type="button" onClick={() => removeAcudiente(index)} className="absolute top-4 right-4 sm:top-6 sm:right-6 text-gray-300 hover:text-red-500 transition-colors bg-white rounded-full p-1 sm:p-0">
                                    <Trash2 size={18} className="sm:w-5 sm:h-5" />
                                </button>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-2 sm:mt-0">
                                <input placeholder="Nombre Completo" required className="w-full p-3.5 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                    value={acudiente.nombre} onChange={e => handleAcudienteChange(index, 'nombre', e.target.value)} />
                                <input placeholder="WhatsApp" required className="w-full p-3.5 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                    value={acudiente.telefono} onChange={e => handleAcudienteChange(index, 'telefono', e.target.value)} />
                                <input placeholder="Parentesco" required className="w-full p-3.5 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                    value={acudiente.parentesco} onChange={e => handleAcudienteChange(index, 'parentesco', e.target.value)} />
                            </div>
                        </div>
                    ))}
                </div>

                <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 sm:py-5 rounded-2xl sm:rounded-[2rem] shadow-2xl shadow-blue-200 transition-all flex items-center justify-center gap-2 active:scale-[0.98]">
                    {loading ? <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-white"></div> : <><Save size={20} className="sm:w-[22px] sm:h-[22px]" /> Finalizar Registro</>}
                </button>
            </form>
        </div>
    );
};

export default RegistroNino;